import { AfterViewInit, Component, ElementRef, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OpportunitySnapshotsService } from '../services/opportunity-snapshots.service';
import { QuestionnaireService } from '../services/questionnaire.service';
import { QuestionnaireModel } from '../models/questionnaire.model';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { SurplusLinesApplicationExplanationQuestion, SurplusLinesApplicationQuestion, SurplusLinesOpportunitySnapshot } from '../models/surplus-lines-opportunity-snapshot.model';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';

export class MyErrorStateMatcher implements ErrorStateMatcher {
  errorOverride: boolean = false;
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    var isSubmitted = form && form.submitted;
    if (this.errorOverride) {
      isSubmitted = true;
    }
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted)) || !!this.errorOverride;
  }
}

export class SSNOrNPIErrorStateMatcher implements ErrorStateMatcher {
  constructor(private ssn: string, private npi: string) { }

  errorOverride: boolean = false;

  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    var isSubmitted = form && form.submitted;
    // If either SSN or NPI is filled, no error state
    return !this.ssn && !this.npi && !!(control?.dirty || control?.touched || isSubmitted) && !!control?.invalid || !!this.errorOverride;
  }
}

@Component({
  selector: 'app-questionnaire',
  standalone: false,
  templateUrl: './questionnaire.component.html',
  styleUrl: '../../assets/css/HighLimits.css'
})

export class QuestionnaireComponent implements OnInit, AfterViewInit {
  matcher: MyErrorStateMatcher = new MyErrorStateMatcher();
  matcher1: MyErrorStateMatcher = new MyErrorStateMatcher();
  matcher2: MyErrorStateMatcher = new MyErrorStateMatcher();
  matcher3: MyErrorStateMatcher = new MyErrorStateMatcher();
  ssnMatcher: SSNOrNPIErrorStateMatcher;
  npiMatcher: SSNOrNPIErrorStateMatcher;
  percent: number = 50;
  currentQuestion: number = 0;
  currentSubsequentQuestion: number = 0;
  currentThirdQuestion: number = 0;
  totalQuestions: number = 0;
  validated: boolean = false;
  snapshot: SurplusLinesOpportunitySnapshot | undefined;
  questions: any[] = [];
  snackBarConfig: MatSnackBarConfig = {
    horizontalPosition: 'center',
    verticalPosition: 'top',
    duration: 5000
  };
  errorMessage: boolean = false;
  typing: boolean = false;
  step: string = 'ssn';
  npi: string = '';
  ssn: string = '';
  heightFeet: string = '';
  heightInches: string = '';
  weight: string = '';
  signVisible: boolean = false;
  paymentVisible: boolean = false;
  noHealthSurvey: boolean = false;

  stepText: { [key: string]: string } = {
    'ssn': `Please provide <strong>ONE</strong> of the below identification numbers.                  `,
    'height': `What is your height and weight?              `,
  }

  constructor(
    private _router: Router,
    private _activeRouter: ActivatedRoute,
    private _questionnaireService: QuestionnaireService,
    private _oss: OpportunitySnapshotsService,
    private _snackbar: MatSnackBar,) {
    this.ssnMatcher = new SSNOrNPIErrorStateMatcher(this.ssn, this.npi);
    this.npiMatcher = new SSNOrNPIErrorStateMatcher(this.ssn, this.npi);
  }

  onFieldChange(): void {
    this.ssnMatcher = new SSNOrNPIErrorStateMatcher(this.ssn, this.npi);
    this.npiMatcher = new SSNOrNPIErrorStateMatcher(this.ssn, this.npi);
  }

  ngOnInit() {
    this.snapshot = this._oss?.currentOpportunitySnapshot as SurplusLinesOpportunitySnapshot;
    var questionnaireId: number;
    if (this._oss.currentQuote && this._oss.currentQuote[0].QuestionnaireId) {
      questionnaireId = Number(this._oss.currentQuote[0].QuestionnaireId);
      if(questionnaireId == 6){
        this.noHealthSurvey = true;
      }
    }

    this._activeRouter.queryParams.subscribe((params) => {
      questionnaireId = params['id'];
      if (questionnaireId) {
        this.getTemplateDetails(questionnaireId);
        if(questionnaireId == 6){
          this.noHealthSurvey = true;
        }
      } else {
        this.getTemplateDetails();
      }
    });

    this.ssn = this._oss.currentApplicant.SSN;
    this.npi = this._oss.currentApplicant.NPI;
    this.heightFeet = this._oss.currentOpportunitySnapshot.HeightFt;
    this.heightInches = this._oss.currentOpportunitySnapshot.HeightIn;
    this.weight = this._oss.currentOpportunitySnapshot.WeightLbs;

    this.paymentVisible = this.paymentVisible = this._oss.currentOpportunitySnapshot.Questions.some(q => {
            const requiresAnswer = q.AnswerType == 'None'
            const hasNoBoolean = q.AnswerBoolean == null;
            const hasNoText = q.AnswerText == null || q.AnswerText.toString().trim() === '';

            return requiresAnswer || ( hasNoBoolean || hasNoText );
          });
    this.signVisible = (this._oss.currentOpportunitySnapshot.PaymentAccountNum || this._oss.currentOpportunitySnapshot.CreditCardPaymentMethodId) ? true : false;
  }

  ngAfterViewInit(): void {
    this.questions.forEach((question: SurplusLinesApplicationQuestion) => {
      this._oss.currentOpportunitySnapshot.Questions.forEach((q: SurplusLinesApplicationQuestion) => {
        if (q.Section == question.Section && q.QuestionNum == question.QuestionNum) {
          question.AnswerBoolean = q.AnswerBoolean;
          question.AnswerText = q.AnswerText;
        }
      });
    });
    this.prelimTypeEffect('ssn');
  }


  save() {
  const current = this.questions[this.currentQuestion];
  if (current) {
      if (current.AnswerBoolean === true && current.ExplanationQuestions?.length) {
        for (const ex of current.ExplanationQuestions) {
          if (!ex.AnswerText || !ex.AnswerText.trim()) {
            this.errorMessage = true;
            this.matcher.errorOverride = true;
            return;
          }
        }
      }

      const answeredMain =
        current.AnswerBoolean !== undefined ||
        current.AnswerText != null ||
        current.AnswerType === 'None';

      if (!answeredMain) {
        this.errorMessage = true;
        return;
      }

      this.errorMessage = false;
    }

    if (this.questions.length > 0) {
      this.snapshot = this._oss.currentOpportunitySnapshot as SurplusLinesOpportunitySnapshot;
      var details = this._questionnaireService.currentTemplateDetails;
      var questionOrder = 1;
      let renewedQuestions : SurplusLinesApplicationQuestion[] = [];
      this.questions.forEach(q => {
        q.questionOrder = questionOrder++;
        let question = this.snapshot?.Questions.find(sq => sq.Section == q.Section && sq.QuestionNum == q.QuestionNum) as SurplusLinesApplicationQuestion;

        if (!question) {
          question = q;
          question.SectionName = details.find(x => x.SectionId == question.Section)?.Section || "";
          question.SectionSubText =  details.find(x => x.SectionId == question.Section)?.SectionSubText || "";
          question.SnapshotId = this.snapshot?.Id as number;
        } else {
          Object.assign(question, q);
          question.SectionName = details.find(x => x.SectionId == question.Section)?.Section || "";
          question.SectionSubText = details.find(x => x.SectionId == question.Section)?.SectionSubText || "";
          question.SnapshotId = this.snapshot?.Id as number;
        }
        renewedQuestions.push(question);
        let explanationQuestionsList: SurplusLinesApplicationQuestion[] = [];
        if (q.ExplanationQuestions) {
          q.ExplanationQuestions.forEach((ex: any) => {
            let explanation = question.ExplanationQuestions.find(exq => exq.ExplanationQuestionNum == ex.ExplanationQuestionNum) as SurplusLinesApplicationExplanationQuestion;
            if (!explanation) {
            explanation = { ...ex };
          }

          const isTextAnswer = explanation.AnswerType !== "Boolean" && explanation.AnswerType !== "None";
          if (isTextAnswer) {
            const explanationAsQuestion: SurplusLinesApplicationQuestion = {
              Id: explanation?.Id,
              Section: question?.Section,
              SectionName: question?.SectionName,
              SectionSubText: question?.SectionSubText,
              QuestionNum: explanation.QuestionNum,
              QuestionText: explanation.QuestionText,
              AnswerText: explanation?.AnswerText,
              AnswerType: "Text",
              Indent: (question?.Indent ?? 0) + 1,
              QuestionOrder: questionOrder++,
              NeedsAnswer: true,
              AnswerBoolean: question.AnswerBoolean,
              ConditionDate: '',
              ConditionDuration: '',
              ConditionRecovered: false,
              ConditionTreatment: '',
              FalseHTML: question?.FalseHTML,
              NeedsConditionAnswer: false,
              Show: explanation.AnswerText ? "Yes" : "No",
              SnapshotId: question?.SnapshotId,
              Step: question?.Step,
              TrueHTML: question?.TrueHTML,
              ExplanationQuestions: []
            };

            if (explanation.AnswerText) {
              explanationQuestionsList.push(explanationAsQuestion);
            }
}
          });
          
          explanationQuestionsList.forEach(x => renewedQuestions.push(x));
        }
      });
      if(renewedQuestions.length > 0){
        this.snapshot.Questions = renewedQuestions;
      }
      this.snapshot.Questions.forEach((q, index) => q.QuestionOrder = index + 1);
      
      this._oss.update(this.snapshot as SurplusLinesOpportunitySnapshot);

      if(this.ssn || this.npi){
        var applicantInfo = this._oss.currentApplicant;
        applicantInfo.Token = localStorage.getItem('sponsorToken') as string;
        if(!!this.ssn){
          applicantInfo.SSN = this.ssn;
          applicantInfo.UseSSN = true;
        }
        else{
          applicantInfo.NPI = this.npi;
        }
        applicantInfo.JobTitle = "";
        this._oss.UpdateCompassApplicantInfo(applicantInfo).subscribe((response) => {});
      }

      this._router.navigate(['/payment']);
      return;
    }
  }

  gotoStep(step: string): void {
    switch (step) {
      case 'ssn':
        this.step = step;
        this.prelimTypeEffect(step);
        break;
      case 'height':
        if (this.ssn.length < 9 && this.npi.length < 10) {
          this.ssnMatcher.errorOverride = true;
          this.npiMatcher.errorOverride = true;
          return;
        } else {
          this.ssnMatcher.errorOverride = false;
          this.npiMatcher.errorOverride = false;
        }
        this.step = step;
        this.prelimTypeEffect(step);
        break;
      case 'done':
        if (this.heightFeet == '' || Number(this.heightFeet) > 10 || Number(this.heightFeet) < 2) {
          this.matcher1.errorOverride = true;
        } else {
          this.matcher1.errorOverride = false;
        }
        if (this.heightInches == '' || Number(this.heightInches) < 0 || Number(this.heightInches) > 11) {
          this.matcher2.errorOverride = true;
        } else {
          this.matcher2.errorOverride = false;
        }
        if (this.weight == '' || Number(this.weight) < 50 || Number(this.weight) > 1000) {
          this.matcher3.errorOverride = true;
        } else {
          this.matcher3.errorOverride = false;
        }
        if (this.matcher1.errorOverride || this.matcher2.errorOverride || this.matcher3.errorOverride) {
          return;
        }
        var oss = this._oss.currentOpportunitySnapshot;
        var applicant = this._oss.currentApplicant;
        applicant.SSN = this.ssn;
        applicant.NPI = this.npi;
        oss.HeightFt = this.heightFeet;
        oss.HeightIn = this.heightInches;
        oss.WeightLbs = this.weight;
        this._oss.updateApplicantInfo(applicant).subscribe((response) => {
        });
        this._oss.update(oss);
        this.step = step;
        setTimeout(() => {
          this.typeEffect();
        }, 2000);
        break;
      case 'doneHNM':
        this.matcher1.errorOverride = false;
        this.matcher2.errorOverride = false;
        this.matcher3.errorOverride = false;

        var oss = this._oss.currentOpportunitySnapshot;
        var applicant2 = this._oss.currentApplicant;
        applicant2.SSN = this.ssn;
        applicant2.NPI = this.npi;

        this._oss.updateApplicantInfo(applicant2).subscribe(() => {});
        this._oss.update(oss);
        this.step = 'done';
        setTimeout(() => this.typeEffect(), 2000);
      break;
      default:
        this.step = 'height';
        this.prelimTypeEffect();
        break;
    }
  }

  preventNonNumeric(event: KeyboardEvent): void {
    const charCode = event.key.charCodeAt(0);
    if (event.key !== 'Backspace' && event.key !== '.' && event.key !== 'Tab' && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
    }
  }

  prelimTypeEffect(step: string = 'ssn'): void {
    this.typing = true;
    var text = this.stepText[step];
    var typingElement = document.querySelector('.prelim-typing-effect') as HTMLElement | null;
    if (typingElement) {
      typingElement.innerHTML = ''; // Clear the content
    }
    let index = 0;
    let lineIndex = 0;
    const lines = text.split('\n');

    const typeLine = () => {
      if (lineIndex < lines.length && typingElement) {
        if (index < lines[lineIndex].length) {
          if (lines[lineIndex].charAt(index) === '<') {
            // Skip the HTML tags
            var tag = '';
            while (lines[lineIndex].charAt(index) !== '>') {
              tag += lines[lineIndex].charAt(index);
              index++;
            }
            tag += lines[lineIndex].charAt(index);
            index++;
            while (lines[lineIndex].charAt(index) !== '>') {
              tag += lines[lineIndex].charAt(index);
              index++;
            }
            tag += lines[lineIndex].charAt(index);
            typingElement.innerHTML += tag; // Use innerHTML to allow HTML rendering
            index++; // Move past the closing '>' of the tag
          }
          typingElement.innerHTML += lines[lineIndex].charAt(index); // Use innerHTML to allow HTML rendering
          index++;
          setTimeout(typeLine, 5); // Adjust typing speed here
        } else {
          typingElement.innerHTML += '<br>'; // Add a line break for new lines
          index = 0;
          lineIndex++;
          setTimeout(typeLine, 500); // Adjust delay between lines here
        }
        this.typing = true;
      } else {
        this.typing = false;
      }
    };

    typeLine();
    this.typing = false;
  }

  typeEffect(questionNumber: number = 0): void {
    if(this.questions.length == 0){
      this.getTemplateDetails();
    }
    var text = this.questions[questionNumber]?.QuestionText;
    var typingElement = document.querySelector('.typing-effect') as HTMLElement | null;
    if (typingElement) {
      typingElement.textContent = '';
    }
    let index = 0;
    let lineIndex = 0;
    const lines = text.split('\n');

    const typeLine = () => {
      this.typing = true;
      if (lineIndex < lines.length && typingElement) {
        if (index < lines[lineIndex].length) {
          typingElement.textContent += lines[lineIndex].charAt(index);
          index++;
          setTimeout(typeLine, 5); // Adjust typing speed here
        } else {
          typingElement.textContent += '\n';
          index = 0;
          lineIndex++;
          setTimeout(typeLine, 500); // Adjust delay between lines here
        }
        this.typing = true;
      } else {
        this.typing = false;
      }
    };

    typeLine();
    this.typing = false;
  }
  CheckSSN() : boolean {
      if (this.ssn.length < 9 && this.npi.length < 10) {
        this.onSSNInput();
        this.ssnMatcher.errorOverride = true;
        this.npiMatcher.errorOverride = true;
        return false;
      } else {
        this.ssnMatcher.errorOverride = false;
        this.npiMatcher.errorOverride = false;
      }
      return true;
    }

  healthQuestionsRouting(forward: boolean){
    if(forward){
      if(!this.CheckSSN()){
        return;
      } 
      
      if(this.noHealthSurvey){
        this.gotoStep('doneHNM');
      }
      else{
        this.gotoStep('height');
      }
    }
    else{
      if(this.noHealthSurvey){
        this.gotoStep('ssn');
        
      }
      else{
        this.gotoStep('height');
      }
    }
  }

  nextSubsequent() {
    this.currentSubsequentQuestion++;
  }

  nextThird() {
    this.currentThirdQuestion++;
  }

  gotoQuestion(questionNumber: number, amount: number): void {
    if (amount < 0) {
      this.errorMessage = false;
      this.matcher.errorOverride = false;

      const target = this.findNextIndex(questionNumber, amount);

      if (target < this.currentQuestion) {
        this.percent = Math.max(0, this.percent - (25 / this.totalQuestions));
      }

      this.currentQuestion = target;
      this.currentSubsequentQuestion = 0;
      this.currentThirdQuestion = 0;
      this.typeEffect(this.currentQuestion);
      return;
    }

    let stop = false;
    this.matcher.errorOverride = false;

    const current = this.questions[this.currentQuestion];
    if (current) {
      if (current.AnswerBoolean === true && current.ExplanationQuestions?.length) {
        for (const ex of current.ExplanationQuestions) {
          if (!ex.AnswerText || !ex.AnswerText.trim()) {
            this.errorMessage = true;
            this.matcher.errorOverride = true;
            stop = true;
            break;
          }
        }
      }

      const answeredMain =
        current.AnswerBoolean !== undefined ||
        current.AnswerText != null ||
        current.AnswerType === 'None';

      if (stop || !answeredMain) {
        this.errorMessage = true;
        return;
      }

      this.errorMessage = false;
    }

    this.percent += (25 / this.totalQuestions);

    const target = this.findNextIndex(questionNumber, amount);
    this.currentQuestion = target;
    this.currentSubsequentQuestion = 0;
    this.currentThirdQuestion = 0;
    this.typeEffect(this.currentQuestion);
  }

  convertToSurplusApplicationQuestions(templateArray: any): SurplusLinesApplicationQuestion[] {
    const questions: SurplusLinesApplicationQuestion[] = [];
    templateArray.map((q: any) => {
      q.ExplanationQuestions.map((eq: any) => {
        questions.push(structuredClone(Object.assign(eq, { Section: q.SectionId, Step: q.Step, ExplanationQuestions: eq.SubExplanationQuestions.map((i: any, idx: any) => ({ Id: i.Id || null, QuestionId: i.QuestionId || null, ExplanationQuestionNum: `${idx + 1}`, QuestionNum: eq.QuestionNum || null, QuestionText: i.QuestionText, AnswerText: i.AnswerText || null, Show: i.Show, AnswerType: i.AnswerType, })), ...eq })) as SurplusLinesApplicationQuestion)
        eq.SubExplanationQuestions.map((seq: any) => {
          if (seq.Show === 'Always' || seq.ThirdExplanationQuestions.length) {
            questions.push(structuredClone(Object.assign(seq, { Section: q.SectionId, Step: q.Step, ExplanationQuestions: seq.ThirdExplanationQuestions.map((i: any, idx: any) => ({ Id: i.Id || null, QuestionId: i.QuestionId || null, ExplanationQuestionNum: `${idx + 1}`, QuestionNum: seq.QuestionNum, QuestionText: i.QuestionText, AnswerText: i.AnswerText || null, Show: i.Show, AnswerType: i.AnswerType, })), ...seq })) as SurplusLinesApplicationQuestion)
          }
        })
      })
    })
    return questions
  }

    lowercaseFirstLetter(text: string): string {
    if (!text) return text;
    return text.charAt(0).toLowerCase() + text.slice(1);
  }

  getTemplateDetails(questionnaireId: number = Number(this._oss.currentQuote[0].QuestionnaireId), update: boolean = true) {
    this._questionnaireService.getQuestionnaireTemplate(questionnaireId).subscribe((response) => {
      var templateDetails = JSON.parse(response.TemplateDetails) as QuestionnaireModel[];
      this.questions = this.convertToSurplusApplicationQuestions(templateDetails);
      this.totalQuestions = this.questions.length;
      this.questions.forEach((question: SurplusLinesApplicationQuestion) => {
        if (question.AnswerType == 'None') {
          this.questions.forEach((q: SurplusLinesApplicationQuestion) => {
            if (q.Section == question.Section && q.QuestionNum != question.QuestionNum) {
              q.QuestionText = `${question.QuestionText.replace('you:', 'you').replace('application:', 'application,').replace(':', ',')} ${this.lowercaseFirstLetter(q.QuestionText)}`;
            }
          });
        } else {
          this._oss.currentOpportunitySnapshot.Questions.forEach((q: SurplusLinesApplicationQuestion) => {
            if (q.Section == question.Section && q.QuestionNum == question.QuestionNum) {
              question.AnswerBoolean = q.AnswerBoolean;
              question.AnswerText = q.AnswerText;

              question.ExplanationQuestions.forEach((ex: SurplusLinesApplicationExplanationQuestion) => {
                q.ExplanationQuestions.forEach((answers: SurplusLinesApplicationExplanationQuestion) => {
                  if (ex.ExplanationQuestionNum == answers.ExplanationQuestionNum) {
                    ex.AnswerText = answers.AnswerText;
                  }
                });
              });
            }
          });
        }
      });
      this.typeEffect();
    })
  }

  gotoPay() {
    this._router.navigate(['/payment']);
  }

  gotoSign() {
    this._router.navigate(['/sign']);
  }

  gotoQuestionnaire() {
    this._router.navigate(['/questionnaire']);
  }

  gotoQuote() {
    this._router.navigate(['/quote']);
  }

  isFinishDisabled(): boolean {
    const current = this.questions[this.currentQuestion];
    if (!current) return true;

    const isLast = this.currentQuestion === this.questions.length - 1;
    const answeredYes = current.AnswerBoolean === true;

    const answeredMain =
      current.AnswerBoolean !== undefined ||
      current.AnswerText != null ||
      current.AnswerType === 'None';

    if (!answeredMain) {
      return true; 
    }

    if (isLast && answeredYes && current.ExplanationQuestions?.length > 0) {
      return current.ExplanationQuestions.some(
        (ex: SurplusLinesApplicationExplanationQuestion) =>
          !ex.AnswerText || ex.AnswerText.trim().length === 0
      );
    }

    return false;
  }

  private findNextIndex(from: number, step: number): number {
    let idx = from + step;
    while (
      idx >= 0 &&
      idx < this.questions.length &&
      this.questions[idx].AnswerType === 'None'
    ) {
      idx += step;
    }

  return Math.max(0, Math.min(this.questions.length - 1, idx));
}

onSSNInput(): void {
  if (this.npi) {
    this.npiMatcher.errorOverride = false;
  }
  if (this.ssn) {
    this.ssnMatcher.errorOverride = false;
  }
}

isValidSSN(v?: string): boolean {
  const s = (v ?? '').trim();
  return /^\d{9}$/.test(s);
}

isValidNPI(v?: string): boolean {
  const s = (v ?? '').trim();
  return /^\d{10}$/.test(s);
}

onHeightInput(): void {
  if (this.heightFeet) {
    this.matcher1.errorOverride = false;
  }
  if (this.heightInches) {
    this.matcher2.errorOverride = false;
  }
  if (this.weight) {
    this.matcher3.errorOverride = false;
  }
}

onTextareaInput(): void {
  this.matcher.errorOverride = false;
}

}